import type { UseFormReturn } from 'react-hook-form';
import { FormField, FormSection } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { numberFieldOptions, textFieldOptions } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { POWERTRAIN_TYPES, type VehicleFormValues } from './vehicle-display';

export interface VehicleFormFieldsProps {
  form: UseFormReturn<VehicleFormValues>;
  segmentOptions: readonly SelectOption[];
  /** Codes are natural keys, so the edit sheet renders the field read-only instead of hiding it. */
  isCodeLocked?: boolean;
}

const TIRE_POSITIONS = [
  { key: 'fl', labelKey: 'vehicles.specs.frontLeft' },
  { key: 'fr', labelKey: 'vehicles.specs.frontRight' },
  { key: 'rl', labelKey: 'vehicles.specs.rearLeft' },
  { key: 'rr', labelKey: 'vehicles.specs.rearRight' },
] as const;

/** Every spec column of `vehicles`, shared by the add and edit sheets so the two cannot drift. */
export const VehicleFormFields = ({ form, segmentOptions, isCodeLocked = false }: VehicleFormFieldsProps) => {
  const { t } = useI18nTranslation();
  const {
    register,
    formState: { errors },
  } = form;

  const powertrainOptions: SelectOption[] = POWERTRAIN_TYPES.map((value) => ({ value, label: t(`enums.powertrain.${value}`) }));

  return (
    <div className="space-y-6">
      <FormSection title={t('vehicles.form.identity')}>
        <FormField
          id="code"
          label={t('vehicles.form.code')}
          error={errors.code?.message}
          hint={isCodeLocked ? t('vehicles.form.codeLocked') : undefined}
        >
          <Input
            id="code"
            readOnly={isCodeLocked}
            disabled={isCodeLocked}
            placeholder={t('vehicles.form.codePlaceholder')}
            hasError={Boolean(errors.code)}
            className="font-mono"
            {...register('code')}
          />
        </FormField>

        <FormField
          id="segmentId"
          label={t('vehicles.form.segment')}
          error={errors.segmentId?.message}
        >
          <Select
            id="segmentId"
            options={segmentOptions}
            placeholder={t('vehicles.form.segmentPlaceholder')}
            hasError={Boolean(errors.segmentId)}
            {...register('segmentId')}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="manufacturer"
            label={t('vehicles.form.manufacturer')}
            error={errors.manufacturer?.message}
          >
            <Input
              id="manufacturer"
              placeholder={t('vehicles.form.manufacturerPlaceholder')}
              hasError={Boolean(errors.manufacturer)}
              {...register('manufacturer')}
            />
          </FormField>

          <FormField
            id="modelYear"
            label={t('vehicles.form.modelYear')}
            error={errors.modelYear?.message}
          >
            <Input
              id="modelYear"
              type="number"
              inputMode="numeric"
              hasError={Boolean(errors.modelYear)}
              {...register('modelYear', numberFieldOptions)}
            />
          </FormField>
        </div>

        <FormField
          id="model"
          label={t('vehicles.form.model')}
          error={errors.model?.message}
        >
          <Input
            id="model"
            placeholder={t('vehicles.form.modelPlaceholder')}
            hasError={Boolean(errors.model)}
            {...register('model')}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="trim"
            label={t('vehicles.form.trim')}
            error={errors.trim?.message}
          >
            <Input
              id="trim"
              hasError={Boolean(errors.trim)}
              {...register('trim', textFieldOptions)}
            />
          </FormField>

          <FormField
            id="vin"
            label={t('vehicles.form.vin')}
            error={errors.vin?.message}
          >
            <Input
              id="vin"
              className="font-mono"
              hasError={Boolean(errors.vin)}
              {...register('vin', textFieldOptions)}
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection title={t('vehicles.form.powertrain')}>
        <FormField
          id="powertrainType"
          label={t('vehicles.form.powertrainType')}
          error={errors.powertrainType?.message}
        >
          <Select
            id="powertrainType"
            options={powertrainOptions}
            hasError={Boolean(errors.powertrainType)}
            {...register('powertrainType')}
          />
        </FormField>

        <FormField
          id="engine"
          label={t('vehicles.form.engine')}
          error={errors.engine?.message}
        >
          <Input
            id="engine"
            placeholder={t('vehicles.form.enginePlaceholder')}
            hasError={Boolean(errors.engine)}
            {...register('engine', textFieldOptions)}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="powerHp"
            label={t('vehicles.form.powerHp')}
            error={errors.powerHp?.message}
          >
            <Input
              id="powerHp"
              type="number"
              inputMode="numeric"
              hasError={Boolean(errors.powerHp)}
              {...register('powerHp', numberFieldOptions)}
            />
          </FormField>

          <FormField
            id="torqueNm"
            label={t('vehicles.form.torqueNm')}
            error={errors.torqueNm?.message}
          >
            <Input
              id="torqueNm"
              type="number"
              inputMode="numeric"
              hasError={Boolean(errors.torqueNm)}
              {...register('torqueNm', numberFieldOptions)}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="transmission"
            label={t('vehicles.form.transmission')}
            error={errors.transmission?.message}
          >
            <Input
              id="transmission"
              placeholder={t('vehicles.form.transmissionPlaceholder')}
              hasError={Boolean(errors.transmission)}
              {...register('transmission', textFieldOptions)}
            />
          </FormField>

          <FormField
            id="drivetrain"
            label={t('vehicles.form.drivetrain')}
            error={errors.drivetrain?.message}
          >
            <Input
              id="drivetrain"
              placeholder={t('vehicles.form.drivetrainPlaceholder')}
              hasError={Boolean(errors.drivetrain)}
              {...register('drivetrain', textFieldOptions)}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="fuelType"
            label={t('vehicles.form.fuelType')}
            error={errors.fuelType?.message}
          >
            <Input
              id="fuelType"
              placeholder={t('vehicles.form.fuelTypePlaceholder')}
              hasError={Boolean(errors.fuelType)}
              {...register('fuelType', textFieldOptions)}
            />
          </FormField>

          <FormField
            id="defaultFuelGrade"
            label={t('vehicles.form.defaultFuelGrade')}
            error={errors.defaultFuelGrade?.message}
          >
            <Input
              id="defaultFuelGrade"
              placeholder={t('vehicles.form.defaultFuelGradePlaceholder')}
              hasError={Boolean(errors.defaultFuelGrade)}
              {...register('defaultFuelGrade', textFieldOptions)}
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection title={t('vehicles.form.chassis')}>
        <FormField
          id="tireSize"
          label={t('vehicles.form.tireSize')}
          error={errors.tireSize?.message}
        >
          <Input
            id="tireSize"
            placeholder={t('vehicles.form.tireSizePlaceholder')}
            hasError={Boolean(errors.tireSize)}
            {...register('tireSize', textFieldOptions)}
          />
        </FormField>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-card-foreground">{t('vehicles.form.tirePressure')}</legend>
          <div className="grid grid-cols-2 gap-3">
            {TIRE_POSITIONS.map(({ key, labelKey }) => (
              <FormField
                key={key}
                id={`pressure-${key}`}
                label={t(labelKey)}
                error={errors.oemTirePressureKpa?.[key]?.message}
              >
                <Input
                  id={`pressure-${key}`}
                  type="number"
                  inputMode="numeric"
                  hasError={Boolean(errors.oemTirePressureKpa?.[key])}
                  {...register(`oemTirePressureKpa.${key}`, numberFieldOptions)}
                />
              </FormField>
            ))}
          </div>
        </fieldset>
      </FormSection>

      <FormSection title={t('vehicles.form.other')}>
        <FormField
          id="odometerKm"
          label={t('vehicles.form.odometerKm')}
          error={errors.odometerKm?.message}
        >
          <Input
            id="odometerKm"
            type="number"
            inputMode="numeric"
            hasError={Boolean(errors.odometerKm)}
            {...register('odometerKm', numberFieldOptions)}
          />
        </FormField>

        <FormField
          id="notes"
          label={t('vehicles.form.notes')}
          error={errors.notes?.message}
        >
          <textarea
            id="notes"
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            {...register('notes', textFieldOptions)}
          />
        </FormField>
      </FormSection>
    </div>
  );
};
